export type EventCalendarView = 'upcoming' | 'past' | 'month' | 'all';

export type CalendarViewEvent = {
  startsAt?: string | Date | null;
};

function toTime(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

export function monthBounds(reference: Date = new Date()): { start: Date; end: Date } {
  const start = new Date(reference.getFullYear(), reference.getMonth(), 1);
  const end = new Date(reference.getFullYear(), reference.getMonth() + 1, 1);
  return { start, end };
}

/** Partition events for Studio/public/Circle calendar tabs (single occurrence; no series). */
export function filterEventsByCalendarView<T extends CalendarViewEvent>(
  events: T[],
  view: EventCalendarView,
  now: Date = new Date(),
): T[] {
  const nowMs = now.getTime();
  if (view === 'all') return events;

  if (view === 'month') {
    const { start, end } = monthBounds(now);
    const startMs = start.getTime();
    const endMs = end.getTime();
    return events.filter((event) => {
      const time = toTime(event.startsAt);
      return time != null && time >= startMs && time < endMs;
    });
  }

  if (view === 'upcoming') {
    return events.filter((event) => {
      const time = toTime(event.startsAt);
      return time == null || time >= nowMs;
    });
  }

  // past
  return events.filter((event) => {
    const time = toTime(event.startsAt);
    return time != null && time < nowMs;
  });
}

export type MonthCalendarDay<T extends CalendarViewEvent> = {
  date: Date;
  inMonth: boolean;
  events: T[];
};

/** Build a 6-week month grid with events grouped by local calendar day. */
export function buildEventMonthCalendarGrid<T extends CalendarViewEvent & { title?: string }>(
  events: T[],
  reference: Date = new Date(),
): MonthCalendarDay<T>[] {
  const { start: monthStart, end: monthEnd } = monthBounds(reference);
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const days: MonthCalendarDay<T>[] = [];

  for (let offset = 0; offset < 42; offset += 1) {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + offset);
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const dayEnd = dayStart + 24 * 60 * 60 * 1000;
    const dayEvents = events.filter((event) => {
      const time = toTime(event.startsAt);
      return time != null && time >= dayStart && time < dayEnd;
    });
    days.push({
      date,
      inMonth: date >= monthStart && date < monthEnd,
      events: dayEvents,
    });
  }

  return days;
}
